import { supabase } from '../supabase';
import type { Property } from '../types/property';
// Removed uuid import since we're not generating IDs anymore



/**
 * Round lot size values to reasonable precision based on the unit
 * This prevents excessive decimal places like "0.382300000 acres"
 */
function roundLotSize(lotSize: number | null | undefined): number | null {
  if (lotSize === null || lotSize === undefined) {
    return null;
  }
  
  // For very small values (likely acres), round to 3 decimal places
  if (lotSize < 1) {
    return Math.round(lotSize * 1000) / 1000; // 3 decimal places
  }
  
  // For medium values (likely square feet), round to 1 decimal place
  if (lotSize < 10000) {
    return Math.round(lotSize * 10) / 10; // 1 decimal place
  }
  
  // For large values, round to whole numbers
  return Math.round(lotSize);
}

/**
 * Round numeric values to reasonable precision
 * This ensures consistent data quality across all numeric fields
 */
function roundNumericValue(value: number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  
  // For whole numbers (like bedrooms, bathrooms, year built), round to integers
  if (Number.isInteger(value)) {
    return value;
  }
  
  // For decimal values, round to 2 decimal places max
  return Math.round(value * 100) / 100;
}

/**
 * Upsert property to database
 */
export async function upsertProperty(property: Property): Promise<void> {

	try {
		// Skip REST API (requires auth) and go straight to Supabase SDK
		// Start with minimal required fields to avoid 400 errors
		const propertyToUpsert = {
			source_id: property.source_id,
			source: property.source ?? "zillow",
			title: property.title ?? null,
			address: property.address ?? null,
			city: property.city ?? null,
			state: property.state ?? null,
			zipcode: property.zipCode ?? null,
			price: property.price ?? null,
			bedrooms: property.details?.bedrooms ?? null,
			bathrooms: property.details?.bathrooms ?? null,
			square_feet: property.details?.squareFeet ?? null,
			year_built: property.details?.yearBuilt ?? null,
			lot_size: (() => {
				const amt = property.details?.lotSize?.amount;
				return typeof amt === 'number' && Number.isFinite(amt) ? amt : null;
			})(),
			type: property.type ?? 'Unknown',
			status: property.status ?? null,
			latitude: property.location?.lat ?? null,
			longitude: property.location?.lon ?? null,
			url: property.url ?? null,
			imageurl: property.imageUrl ?? null,
			description: property.description ?? null,
			days_on_market: (() => {
				const val = (property as any).daysOnMarket;
				if (typeof val === 'number' && Number.isFinite(val)) return val;
				if (typeof val === 'string') {
					const m = val.match(/\d+/);
					return m ? parseInt(m[0], 10) : null;
				}
				return null;
			})(),
			last_sync: new Date().toISOString()
		};

		// Use REST bypass to prevent SDK hanging issues
		const { supabaseUrl, supabaseAnonKey } = await import('../supabase');
		const restUrl = `${supabaseUrl}/rest/v1/properties?on_conflict=source_id`;
		
		const resp = await fetch(restUrl, {
			method: 'POST',
			headers: {
				'apikey': supabaseAnonKey,
				'Authorization': `Bearer ${supabaseAnonKey}`,
				'Content-Type': 'application/json',
				'Prefer': 'resolution=merge-duplicates'
			},
			body: JSON.stringify([propertyToUpsert])
		});
		
		if (!resp.ok) {
			const errorText = await resp.text().catch(() => '');
			throw new Error(`REST upsert failed with status ${resp.status}: ${errorText}`);
		}
	} catch (error) {
		// Property upsert failed
	}
}

export async function transformDbPropertyToInterface(property: any): Promise<Property> {
  // Processing property
  
  // Check if this is a coming soon property that needs photo refresh
  if (property.status === 'Coming Soon' && (!property.photos || property.photos.length === 0)) {
    try {
      // Import the function here to avoid circular dependencies
      const { refreshComingSoonPropertyPhotosOnRetrieval } = await import('./photo-extraction');
      const refreshedProperty = await refreshComingSoonPropertyPhotosOnRetrieval(property);
      
      // Return the refreshed property data
      return {
        id: refreshedProperty.id,
        title: refreshedProperty.title,
        price: refreshedProperty.price,
        address: refreshedProperty.address,
        city: refreshedProperty.city,
        state: refreshedProperty.state,
        type: refreshedProperty.type,
        last_sync: refreshedProperty.last_sync,
        source: refreshedProperty.source,
        source_id: refreshedProperty.source_id,
        details: refreshedProperty.details,
        imageUrl: refreshedProperty.imageUrl,
        location: refreshedProperty.location,
        zipCode: refreshedProperty.zipCode,
        photos: refreshedProperty.photos,
        description: refreshedProperty.description,
        status: refreshedProperty.status,
        hoa_dues: refreshedProperty.hoa_dues,
        openHouses: refreshedProperty.openHouses,
        
        url: refreshedProperty.url,
      };
    } catch (error) {
      // Silently handle photo refresh errors
    }
  }
  

  

  const transformedProperty = {
    id: property.id,
    title: property.title,
    price: property.price,
    address: property.address,
    city: property.city,
    state: property.state,
    type: property.type,
    last_sync: property.last_sync,
    source: property.source ?? "zillow",
    source_id: property.source_id,
    details: {
      // Map database columns back to the details object structure
      bedrooms: property.bedrooms || property.details?.bedrooms,
      bathrooms: property.bathrooms || property.details?.bathrooms,
      squareFeet: property.square_feet || property.details?.squareFeet,
      yearBuilt: property.year_built || property.details?.yearBuilt,
      lotSize: property.lot_size ? { amount: property.lot_size } : property.details?.lotSize,
    },
    // Map database column names to interface property names
    imageUrl: property.imageUrl || property.imageurl, // Handle both possible column names
    location: property.location,
    zipCode: property.zipCode || property.zipcode, // Handle both possible column names
    photos: property.photos,
    description: property.description,
    status: property.status,
    hoa_dues: property.hoa_dues,
    openHouses: property.openHouses || property.openhouses, // Handle both possible column names
    
    url: property.url,
    lot_area_units: property.lot_size_units,
    // Add days on market fields that are missing
    daysOnZillow: property.daysOnZillow || property.days_on_zillow || null,
    timeOnZillow: property.timeOnZillow || property.time_on_zillow || null,
    // Map days_on_zillow to both daysOnZillow and daysOnMarket since database only has days_on_zillow
    daysOnMarket: property.daysOnMarket || property.days_on_market || property.days_on_zillow || null,
  };

  
  return transformedProperty;
}

export async function cacheProperties(properties: Property[]): Promise<void> {

  try {
    // Deduplicate properties by source_id to prevent duplicate upserts
    const uniqueProperties = properties.reduce((acc, property) => {
      if (!acc.some(p => p.source_id === property.source_id)) {
        acc.push(property);
      }
      return acc;
    }, [] as Property[]);
    
    
    // Handle Coming Soon properties before caching
    const processedProperties = await Promise.all(
      uniqueProperties.map(async (property) => {
        // Import the function here to avoid circular dependencies
        const { handleComingSoonProperty } = await import('./photo-extraction');
        return await handleComingSoonProperty(property);
      })
    );

    // Upsert each property individually to track failures
    const upsertResults = [];
    let successCount = 0;
    let failureCount = 0;
    
    for (let i = 0; i < processedProperties.length; i++) {
      const property = processedProperties[i];
      
      try {
        await upsertProperty(property);
        upsertResults.push({ success: true, property: property.source_id });
        successCount++;
      } catch (error) {
        upsertResults.push({ success: false, property: property.source_id, error: error instanceof Error ? error.message : String(error) });
        failureCount++;
      }
    }
    
    // Handle any failures silently - the calling function can handle them if needed
  } catch (error) {
    // Re-throw the error for proper error handling
    throw error;
  }
} 